import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import api from "../Api";
import Popup from "../components/PopUP";

export default function UpdateManufacturer() {
  const { id } = useParams();
  const [formData, setFormData] = useState({
    company_name: '',
    headquarter: '',
    founding_date: '',
    company_description: '',
    company_history: '',
    logo: '',
    cover_photo: {
      url: '',
      license: '',
      author: '',
      wiki: ''
    },
    product_series: [{ id: 1, series: 'Airplane', modelsString: '' }], // default series = Airplane
    interesting_facts: [{ id: 1, fact: '' }]
  });

  const [errors, setErrors] = useState({});
  const [popupMessage, setPopupMessage] = useState("");
  const [loading, setLoading] = useState(true);

  // Fetch manufacturer details on component mount
  useEffect(() => {
    const fetchManufacturerDetails = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/airplane-service/manufacturer/${id}`);
        console.log("Fetched Manufacturer Details:", response.data);

        const data = response.data.data;

        // Transform product data: each item has series name and models array
        const products = data.product.map((product, index) => ({
          id: index + 1,
          series: product.series || 'Airplane',
          modelsString: (product.data || []).join(', ')
        }));

        // Transform interesting facts data (array of strings)
        const facts = data.interesting_facts.map((fact, index) => ({
          id: index + 1,
          fact: fact
        }));

        setFormData({
          company_name: data.general.company_name || '',
          headquarter: data.general.headquarter || '',
          founding_date: data.general.founding_date || '',
          company_description: data.company.company_description || '',
          company_history: data.company.company_history || '',
          logo: data.general.logo || '',
          cover_photo: {
            url: data.general.cover_photo.url || '',
            license: data.general.cover_photo.license || '',
            author: data.general.cover_photo.author || '',
            wiki: data.general.cover_photo.wiki || ''
          },
          product_series: products.length > 0 ? products : [{ id: 1, series: 'Airplane', modelsString: '' }],
          interesting_facts: facts.length > 0 ? facts : [{ id: 1, fact: '' }]
        });

        setLoading(false);
      } catch (error) {
        console.error("Error fetching manufacturer details:", error);
        setPopupMessage("Error loading manufacturer details");
        setLoading(false);
      }
    };

    if (id) {
      fetchManufacturerDetails();
    }
  }, [id]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleCoverPhotoChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      cover_photo: { ...formData.cover_photo, [name]: value }
    });
  };

  const handleProductChange = (id, field, value) => {
    setFormData({
      ...formData,
      product_series: formData.product_series.map(product =>
        product.id === id ? { ...product, [field]: value } : product
      )
    });
  };

  const handleFactChange = (id, value) => {
    setFormData({
      ...formData,
      interesting_facts: formData.interesting_facts.map(fact =>
        fact.id === id ? { ...fact, fact: value } : fact
      )
    });
  };

  const addProduct = () => {
    setFormData({
      ...formData,
      product_series: [...formData.product_series, { id: formData.product_series.length + 1, series: 'Airplane', modelsString: '' }]
    });
  };

  const removeProduct = (id) => {
    if (formData.product_series.length > 1) {
      setFormData({
        ...formData,
        product_series: formData.product_series.filter(product => product.id !== id)
      });
    }
  };

  const addFact = () => {
    setFormData({
      ...formData,
      interesting_facts: [...formData.interesting_facts, { id: formData.interesting_facts.length + 1, fact: '' }]
    });
  };

  const removeFact = (id) => {
    if (formData.interesting_facts.length > 1) {
      setFormData({
        ...formData,
        interesting_facts: formData.interesting_facts.filter(fact => fact.id !== id)
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.company_name.trim()) newErrors.company_name = 'Company name is required';
    if (!formData.headquarter.trim()) newErrors.headquarter = 'Headquarter is required';
    if (!formData.founding_date || !/^\d{4}$/.test(formData.founding_date)) newErrors.founding_date = 'Valid year (YYYY) is required';
    if (!formData.company_description.trim()) newErrors.company_description = 'Description is required';
    if (!formData.company_history.trim()) newErrors.company_history = 'History is required';
    if (!formData.logo.trim()) newErrors.logo = 'Logo URL is required';
    if (!formData.cover_photo.url.trim()) newErrors.cover_photo_url = 'Cover photo URL is required';

    formData.product_series.forEach((product) => {
      if (!product.series) newErrors[`series_${product.id}`] = 'Series name is required';
      if (!product.modelsString.trim()) newErrors[`models_${product.id}`] = 'At least one model is required';
    });

    formData.interesting_facts.forEach((fact) => {
      if (!fact.fact.trim()) newErrors[`fact_${fact.id}`] = 'Fact is required';
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      // Transform product_series: split modelsString into array of trimmed strings
      const productSeriesFormatted = formData.product_series.map(({ id, series, modelsString }) => ({
        series: series.trim(),
        data: modelsString.split(',').map(model => model.trim()).filter(model => model !== '')
      }));

      const postData = {
        company_name: formData.company_name,
        headquarter: formData.headquarter,
        founding_date: formData.founding_date,
        company_description: formData.company_description,
        company_history: formData.company_history,
        logo: formData.logo,
        cover_photo: {
          url: formData.cover_photo.url,
          license: formData.cover_photo.license,
          author: formData.cover_photo.author,
          wiki: formData.cover_photo.wiki
        },
        product_series: productSeriesFormatted,
        interesting_facts: formData.interesting_facts.map(fact => fact.fact)
      };

      console.log("Updating data:", postData);
      try {
        const response = await api.put(`/airplane-service/manufacturer/`, postData);
        console.log(response.data.detail);
        setPopupMessage(response.data.detail || "Manufacturer updated successfully");
      } catch (error) {
        console.log("API Error:", error.response?.data?.detail);
        setPopupMessage(error.response?.data?.detail || 'Error updating manufacturer');
      }
    } else {
      setPopupMessage('Failed to submit form. Please check the errors.');
    }
  };

  if (loading) {
    return (
      <>
        <Sidebar />
        <div className="main-wrapper">
          <Header />
          <div className="content-wrapper">
            <div className="container mt-5">
              <p>Loading manufacturer details...</p>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Sidebar />
      <div className="main-wrapper">
        <Header />

        <Popup
          message={popupMessage}
          onClose={() => setPopupMessage("")}
        />

        <div className="content-wrapper">
          <section className="main-section spacer-y">
            <div className="container">
              <div className="row gy-4">
                <div className="col-xl-12">
                  <div className="common_main_bg_wrapper">
                    <div className="common_main_heading_wrapper">
                      <div className="vstack">
                        <nav className="breadcrumb_nav">
                          <ul className="breadcrumb mb-0">
                            <li className="breadcrumb-item">
                              <Link to="/dashboard">Dashboard</Link>
                            </li>
                            <li className="breadcrumb-item">
                              <Link to="/manufacturer">
                                Manufacturer Management
                              </Link>
                            </li>
                            <li className="breadcrumb-item text-capitalize">
                              Update Manufacturer
                            </li>
                          </ul>
                        </nav>
                        <h1>Manufacturer Management</h1>
                      </div>
                      <div className="d-flex flex-end">
                        <Link to="/manufacturer" className="secondary_btn">
                          <img
                            src="/assets/images/Frame (19).svg"
                            alt="Manufacturer Management"
                          />
                          View All Manufacturers
                        </Link>
                      </div>
                    </div>
                  </div>
                  <form onSubmit={handleSubmit}>
                    <div className="common-outer-card mt-4">
                      <div className="common_sub_heading_wrapper">
                        <h2>Update Manufacturer</h2>
                      </div>
                      <div className="common_content_blk">
                        <div className="row g-4">
                          {/* Manufacturer Name, Headquarter, Founding Date, Logo, Cover Photo ... same as before */}
                          <div className="col-12 col-sm-6 col-md-4">
                            <div className="form_field">
                              <label>Manufacturer Name</label>
                              <div className="input_field">
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder="Enter Manufacturer/Company name"
                                  name="company_name"
                                  value={formData.company_name}
                                  onChange={handleInputChange}
                                />
                                <span className="error">{errors.company_name}</span>
                              </div>
                            </div>
                          </div>

                          <div className="col-12 col-sm-6 col-md-4">
                            <div className="form_field">
                              <label>Headquarter</label>
                              <div className="input_field">
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder="Enter Headquarter"
                                  name="headquarter"
                                  value={formData.headquarter}
                                  onChange={handleInputChange}
                                />
                                <span className="error">{errors.headquarter}</span>
                              </div>
                            </div>
                          </div>

                          <div className="col-12 col-sm-6 col-md-4">
                            <div className="form_field">
                              <label>Founding Date</label>
                              <div className="input_field">
                                <input
                                  type="number"
                                  className="form-control"
                                  placeholder="YYYY"
                                  min="1900"
                                  max="2099"
                                  step="1"
                                  name="founding_date"
                                  value={formData.founding_date}
                                  onChange={handleInputChange}
                                />
                                <span className="error">{errors.founding_date}</span>
                              </div>
                            </div>
                          </div>

                          <div className="col-12 col-sm-6 col-md-6">
                            <div className="form_field">
                              <label>Logo (URL)</label>
                              <div className="input_field">
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder="Enter image URL ..."
                                  name="logo"
                                  value={formData.logo}
                                  onChange={handleInputChange}
                                />
                                <span className="error">{errors.logo}</span>
                              </div>
                            </div>
                          </div>

                          <div className="col-12 col-sm-6 col-md-6">
                            <div className="form_field">
                              <label>Cover/Background Image (URL)</label>
                              <div className="input_field">
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder="Enter image URL ..."
                                  name="url"
                                  value={formData.cover_photo.url}
                                  onChange={handleCoverPhotoChange}
                                />
                                <span className="error">{errors.cover_photo_url}</span>
                              </div>
                            </div>
                          </div>

                          <div className="col-12 col-sm-6 col-md-4">
                            <div className="form_field">
                              <label>License (Optional)</label>
                              <div className="input_field">
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder="Enter License detail..."
                                  name="license"
                                  value={formData.cover_photo.license}
                                  onChange={handleCoverPhotoChange}
                                />
                              </div>
                            </div>
                          </div>

                          <div className="col-12 col-sm-6 col-md-4">
                            <div className="form_field">
                              <label>Author (Optional)</label>
                              <div className="input_field">
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder="Enter Author detail..."
                                  name="author"
                                  value={formData.cover_photo.author}
                                  onChange={handleCoverPhotoChange}
                                />
                              </div>
                            </div>
                          </div>

                          <div className="col-12 col-sm-6 col-md-4">
                            <div className="form_field">
                              <label>Wiki (Optional)</label>
                              <div className="input_field">
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder="Enter Wiki URL ..."
                                  name="wiki"
                                  value={formData.cover_photo.wiki}
                                  onChange={handleCoverPhotoChange}
                                />
                              </div>
                            </div>
                          </div>

                          <div className="col-12 col-sm-12 col-md-12">
                            <div className="form_field">
                              <label>Company/Manufacturer Description</label>
                              <div className="input_field">
                                <textarea
                                  className="form-control"
                                  rows="4"
                                  placeholder="Write something..."
                                  name="company_description"
                                  value={formData.company_description}
                                  onChange={handleInputChange}
                                />
                                <span className="error">{errors.company_description}</span>
                              </div>
                            </div>
                          </div>

                          <div className="col-12 col-sm-12 col-md-12">
                            <div className="form_field">
                              <label>Company/Manufacturer History</label>
                              <div className="input_field">
                                <textarea
                                  className="form-control"
                                  rows="4"
                                  placeholder="Write something ..."
                                  name="company_history"
                                  value={formData.company_history}
                                  onChange={handleInputChange}
                                />
                                <span className="error">{errors.company_history}</span>
                              </div>
                            </div>
                          </div>

                          {/* Product Series Section with Select Dropdown */}
                          {formData.product_series.map((product) => (
                            <div key={product.id} className="card p-3 mb-3">
                              <div className="row mb-3">
                                <div className="col-12 col-sm-6 col-md-11">
                                  <div className="form_field">
                                    <label>Series Name</label>
                                    <div className="input_field">
                                      <select
                                        className="form-control"
                                        value={product.series}
                                        onChange={(e) => handleProductChange(product.id, 'series', e.target.value)}
                                      >
                                        <option value="Airplane">Airplane</option>
                                        <option value="Helicopter">Helicopter</option>
                                      </select>
                                      <span className="error">{errors[`series_${product.id}`]}</span>
                                    </div>
                                  </div>
                                </div>
                                <div className="col-1 d-flex align-items-center">
                                  {formData.product_series.length > 1 && (
                                    <button
                                      type="button"
                                      className="btn btn-danger rounded-pill"
                                      onClick={() => removeProduct(product.id)}
                                    >
                                      Remove
                                    </button>
                                  )}
                                </div>
                              </div>
                              <div className="row">
                                <div className="col-12">
                                  <div className="form_field">
                                    <label>Models (comma separated)</label>
                                    <div className="input_field">
                                      <textarea
                                        className="form-control"
                                        rows="3"
                                        placeholder="Enter model names separated by commas, e.g.: Model A, Model B"
                                        value={product.modelsString}
                                        onChange={(e) => handleProductChange(product.id, 'modelsString', e.target.value)}
                                      />
                                      <span className="error">{errors[`models_${product.id}`]}</span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}

                          <div className="col-12 col-sm-12 col-md-12">
                            <button
                              type="button"
                              className="primary-btn rounded-pill"
                              onClick={addProduct}
                            >
                              Add Another Series
                            </button>
                          </div>

                          {/* Interesting Facts Section */}
                          {formData.interesting_facts.map((fact) => (
                            <div className="row my-3" key={fact.id}>
                              <div className="col-12 col-sm-12 col-md-11">
                                <div className="form_field">
                                  <label>Interesting Fact</label>
                                  <div className="input_field">
                                    <textarea
                                      className="form-control"
                                      rows="4"
                                      placeholder="Write Fact ..."
                                      value={fact.fact}
                                      onChange={(e) => handleFactChange(fact.id, e.target.value)}
                                    />
                                    <span className="error">{errors[`fact_${fact.id}`]}</span>
                                  </div>
                                </div>
                              </div>
                              <div className="col-12 col-sm-12 col-md-1 d-flex align-items-center">
                                {formData.interesting_facts.length > 1 && (
                                  <button
                                    type="button"
                                    className="btn btn-danger rounded-pill"
                                    onClick={() => removeFact(fact.id)}
                                  >
                                    Remove
                                  </button>
                                )}
                              </div>
                            </div>
                          ))}

                          <div className="col-12 col-sm-12 col-md-12">
                            <button
                              type="button"
                              className="primary-btn rounded-pill"
                              onClick={addFact}
                            >
                              Add Another Fact
                            </button>
                          </div>

                          <div className="main_action_blk hstack gap-3 justify-content-start">
                            <Link to="/manufacturer" className="bg_primary_outline_btn rounded-pill">
                              Cancel
                            </Link>
                            <button type="submit" className="primary-btn rounded-pill">
                              Update
                            </button>
                            {errors.submit && <span className="error">{errors.submit}</span>}
                          </div>
                        </div>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
}